import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, FormControl, InputLabel, MenuItem, Paper, Select, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import currencyFormatter from '~/utils/currencyFormatter';

import PrintableChip from './PrintableChip';
import SupplierFilterChips from './SupplierFilterChips';
import { recomputeAnalysis } from './analyzeParityCsv';
import { ALL_SEVERITY_FILTER, SECTION_SX, SEVERITY_COLORS, SUPPLIER_CHIP_COLORS } from './constants';
import { ParityAnalysis, SeverityLevel } from './types';
import { deriveActiveTableOtas, deriveUniqueCompetitors, deriveUniqueSeverities, filterDateRanges } from './utils';

interface ProblemDateRangesSectionProps {
  analysis: ParityAnalysis;
}

export default function ProblemDateRangesSection({ analysis }: ProblemDateRangesSectionProps) {
  const fmtPrice = useCallback((n: number) => currencyFormatter(analysis.currency, n, 2), [analysis.currency]);

  const uniqueCompetitors = useMemo(() => deriveUniqueCompetitors(analysis.dailyDetail), [analysis.dailyDetail]);
  const uniqueSeverities = useMemo(() => deriveUniqueSeverities(analysis.dateRanges), [analysis.dateRanges]);

  const [competitorVisibility, setCompetitorVisibility] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const c of uniqueCompetitors) {
      init[c] = true;
    }
    return init;
  });

  useEffect(() => {
    const init: Record<string, boolean> = {};
    for (const c of uniqueCompetitors) {
      init[c] = true;
    }
    setCompetitorVisibility(init);
  }, [uniqueCompetitors]);

  const [severityFilter, setSeverityFilter] = useState<string>(ALL_SEVERITY_FILTER);

  const toggleCompetitor = (comp: string) => {
    setCompetitorVisibility((prev) => ({ ...prev, [comp]: !prev[comp] }));
  };

  const activeTableOtas = useMemo(
    () => deriveActiveTableOtas(competitorVisibility, uniqueCompetitors),
    [competitorVisibility, uniqueCompetitors],
  );

  const tableAnalysis = useMemo(() => recomputeAnalysis(analysis, activeTableOtas), [analysis, activeTableOtas]);

  const filteredDateRanges = useMemo(
    () => filterDateRanges(tableAnalysis.dateRanges, severityFilter),
    [tableAnalysis.dateRanges, severityFilter],
  );

  const dateRangeCols = useMemo<Array<GridColDef>>(
    () => [
      {
        field: 'dateRange',
        headerName: 'Date Range',
        flex: 2,
        valueGetter: (_, row) => `${row.startDate} — ${row.endDate}`,
      },
      { field: 'numDays', headerName: 'Days', width: 70 },
      { field: 'avgLePrice', headerName: 'Avg LE Price', width: 120, valueFormatter: (v: number) => fmtPrice(v) },
      {
        field: 'avgCompetitorPrice',
        headerName: 'Avg Competitor',
        width: 130,
        valueFormatter: (v: number) => fmtPrice(v),
      },
      { field: 'avgDiffPct', headerName: 'Gap', width: 80, valueFormatter: (v: number) => `${v}%` },
      {
        field: 'competitor',
        headerName: 'Competitor',
        flex: 1,
        renderCell: (params) => {
          const chipStyle = SUPPLIER_CHIP_COLORS[params.value as string];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {chipStyle ? (
                <PrintableChip label={params.value} bg={chipStyle.bg} color={chipStyle.color} />
              ) : (
                params.value
              )}
            </Box>
          );
        },
      },
      {
        field: 'severity',
        headerName: 'Severity',
        width: 110,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <PrintableChip label={params.value} bg={SEVERITY_COLORS[params.value as SeverityLevel]} />
          </Box>
        ),
      },
    ],
    [fmtPrice],
  );

  return (
    <Paper elevation={0} sx={SECTION_SX}>
      <Typography sx={{ fontSize: 24, fontWeight: 600, mb: 3 }}>Problem Check-in Date Ranges</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={severityFilter}
            label="Severity"
            onChange={(e) => setSeverityFilter(e.target.value)}
            renderValue={(val) =>
              val === ALL_SEVERITY_FILTER ? (
                'All'
              ) : (
                <PrintableChip label={val} bg={SEVERITY_COLORS[val as SeverityLevel]} />
              )
            }
          >
            <MenuItem value={ALL_SEVERITY_FILTER}>All</MenuItem>
            {uniqueSeverities.map((sev) => (
              <MenuItem key={sev} value={sev}>
                <PrintableChip label={sev} bg={SEVERITY_COLORS[sev]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SupplierFilterChips
          suppliers={uniqueCompetitors}
          isActive={(comp) => competitorVisibility[comp]}
          onToggle={toggleCompetitor}
          label="Competitor"
        />
      </Box>
      <DataGrid
        rows={filteredDateRanges.map((r, i) => ({ id: i, ...r }))}
        columns={dateRangeCols}
        autoHeight
        hideFooter={filteredDateRanges.length <= 25}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableColumnMenu
        sx={{
          border: 'none',
          borderRadius: '12px',
          fontSize: 13,
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'grey.50',
            borderRadius: '12px 12px 0 0',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: 13,
            fontWeight: 600,
            color: 'text.secondary',
          },
          '& .MuiDataGrid-cell': {
            fontSize: 13,
            borderColor: 'divider',
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: 'grey.50',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            bgcolor: 'action.hover',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
          },
          '& .MuiDataGrid-withBorderColor': {
            borderColor: 'divider',
          },
        }}
      />
    </Paper>
  );
}
